import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import ExtendedMedicationInfo from 'components/Dashboard/cards/common/ExtendedMedicationInfo'
import {
  PriorChangeRxRequestSchema,
  ChangeRxRequestSchema,
  prescriberChangeRxRequestSchema,
  prescriberChangeStateLicenseRxRequestSchema,
  EmptyRxRequestSchema,
} from 'schemas/ChangeRxRequest'
import Section from '../Section'
import RequestedMedications from '../RequestedMedications'
import InputWithActions from '../InputWithActions'

import PharmacyInfo from '../PharmacyInfo'

type ExpandContentProps = {
  title: string
  address: string
  phone: string
  medication: string
  directions: string
  notes: string
  unit: string
  substitutions: string
  writtenDate: string
  quantity: number
  refills: number
  daysSupply: number
  // TODO - improve requestedMedications type
  requestedMedications: any[]
  isPriorAuthType: boolean
  isPrescriberAuthType: boolean
  showPrescriberLicenseInactiveCheckbox: boolean
  prescriberAuthorizationDataLabel?: string
  prescriberAuthorizationDataType?: string
  putApproveChangeRequest: () => void
  putDenyChangeRequest: () => void
  // TODO - improve prescribedMedication type
  prescribedMedication: any
  // TODO - improve pharmacy props
  pharmacy: any
}

const ExpandContent = ({
  title,
  address,
  phone,
  medication,
  directions,
  notes,
  unit,
  substitutions,
  writtenDate,
  quantity,
  refills,
  daysSupply,
  requestedMedications,
  isPriorAuthType,
  isPrescriberAuthType,
  showPrescriberLicenseInactiveCheckbox,
  putApproveChangeRequest,
  prescriberAuthorizationDataLabel,
  prescriberAuthorizationDataType,
  putDenyChangeRequest,
  pharmacy,
  prescribedMedication,
}: ExpandContentProps) => {
  const schemaSelector = () => {
    if (isPriorAuthType) {
      return PriorChangeRxRequestSchema
    } else if (showPrescriberLicenseInactiveCheckbox) {
      return prescriberChangeStateLicenseRxRequestSchema(prescriberAuthorizationDataLabel)
    } else if (isPrescriberAuthType) {
      return prescriberChangeRxRequestSchema(prescriberAuthorizationDataLabel)
    } else if (requestedMedications.length > 0) {
      return ChangeRxRequestSchema
    } else {
      return EmptyRxRequestSchema
    }
  }

  const methods = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(schemaSelector()),
    defaultValues: {
      priorAuthNumber: '',
      selectedIndex: -1,
      prescriberLicenseInactive: false,
      prescriberAuthorizationData: '',
    },
  })

  return (
    <>
      <hr className="my-4" />
      <Section sectionName={'Prescriber'} title={title} address={address} phone={phone} />
      <hr className="my-4" />
      <PharmacyInfo pharmacy={pharmacy} />
      <hr className="my-4" />
      <p className="typography-caption text-neutral-500">Prescribed medication</p>
      <ExtendedMedicationInfo
        medication={medication}
        directions={directions}
        notes={notes}
        unit={unit}
        substitutions={substitutions}
        writtenDate={writtenDate}
        quantity={quantity}
        refills={refills}
        daysSupply={daysSupply}
      />
      <hr className="my-4" />
      <FormProvider {...methods}>
        <RequestedMedications requestedMedications={requestedMedications} />
        <InputWithActions
          isPriorAuthType={isPriorAuthType}
          putApproveChangeRequest={putApproveChangeRequest}
          putDenyChangeRequest={putDenyChangeRequest}
          prescriberAuthorizationDataLabel={prescriberAuthorizationDataLabel}
          prescriberAuthorizationDataType={prescriberAuthorizationDataType}
          isPrescriberAuthType={isPrescriberAuthType}
          showPrescriberLicenseInactiveCheckbox={showPrescriberLicenseInactiveCheckbox}
          requestedMedications={requestedMedications}
          prescribedMedication={prescribedMedication}
        />
      </FormProvider>
    </>
  )
}

export default ExpandContent
